import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { CalendarCheck, Wallet, Users, Building2, FileText, Mail, Eye } from "lucide-react";
import { ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";
import { Toaster, toast } from "sonner";
import { downloadFile } from "../../lib/download";
import LiveBookingsCard from "../../components/LiveBookingsCard";
import { useAuth } from "../../context/AuthContext";
import { time12h } from "../../lib/dates";

const PERIODS = [
    { value: "7d", label: "Last 7 days" },
    { value: "this_month", label: "This month" },
    { value: "last_month", label: "Last month" },
    { value: "this_year", label: "This year" },
    { value: "custom", label: "Custom range" },
];

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [centres, setCentres] = useState([]);
    const [period, setPeriod] = useState("7d");
    const [centreId, setCentreId] = useState("");
    const [start, setStart] = useState("");
    const [end, setEnd] = useState("");
    const [emailing, setEmailing] = useState(false);
    const [todayOpen, setTodayOpen] = useState(false);
    const [todayCentre, setTodayCentre] = useState("");
    const [todayData, setTodayData] = useState(null);

    const today = new Date().toISOString().slice(0, 10);

    const sendDailyEmail = async () => {
        setEmailing(true);
        try {
            const { data } = await api.post("/admin/close-of-day/email", { date: today });
            toast.success(`Daily report queued — ${data.recipients?.length || 0} recipient(s) (mocked)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        } finally {
            setEmailing(false);
        }
    };

    const openToday = async (filterCentre = "") => {
        setTodayCentre(filterCentre);
        setTodayOpen(true);
        const params = filterCentre ? { centre_id: filterCentre } : {};
        const { data } = await api.get("/admin/today", { params });
        setTodayData(data);
    };

    const refreshToday = async (filterCentre) => {
        const params = filterCentre ? { centre_id: filterCentre } : {};
        const { data } = await api.get("/admin/today", { params });
        setTodayData(data);
    };

    useEffect(() => {
        api.get("/centres").then(({ data }) => setCentres(data));
    }, []);

    useEffect(() => {
        const params = { period };
        if (centreId) params.centre_id = centreId;
        if (period === "custom") {
            if (!start || !end) return;
            params.start_date = start;
            params.end_date = end;
        }
        const fetchStats = () => {
            api.get("/admin/stats", { params }).then(({ data }) => setStats(data)).catch(() => {});
        };
        fetchStats();
        // Auto-refresh KPIs every 30 s so Today's Revenue picks up new bookings without a page reload
        const id = setInterval(fetchStats, 30000);
        // Also re-fetch when the tab regains focus (instant feedback after a checkout in another tab)
        const onFocus = () => fetchStats();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, [period, centreId, start, end]);

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Command Center</h1>
                        <p className="text-slate-400 text-sm mt-1">Live snapshot of operations across centres.</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <PreviewAsUserButton />
                        <button onClick={() => downloadFile("/admin/close-of-day.pdf", `close-of-day-${today}.pdf`, { date: today })}
                            data-testid="cod-download-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-slate-200 text-slate-900 font-bold uppercase tracking-widest text-xs rounded hover:bg-white">
                            <FileText className="w-4 h-4" /> Daily PDF
                        </button>
                        <button onClick={sendDailyEmail} disabled={emailing} data-testid="cod-email-btn"
                            className="inline-flex items-center gap-2 px-3 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52] disabled:opacity-40">
                            <Mail className="w-4 h-4" /> {emailing ? "Sending..." : "Email Report"}
                        </button>
                    </div>
                </div>

                {!stats ? <div className="text-slate-500 mt-8">Loading...</div> : (
                    <>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-8">
                            <Kpi label="Today's Bookings" value={stats.today_bookings} icon={CalendarCheck} testid="kpi-today-bookings" onClick={() => openToday("")} hint="View details" />
                            <Kpi label="Today's Revenue" value={`₹${stats.today_total_revenue_incl_pending ?? stats.today_revenue}`} icon={Wallet} testid="kpi-today-revenue" highlight onClick={() => openToday("")} hint={`${stats.today_completed_bookings || 0} of ${stats.today_bookings} completed`} />
                            <Kpi label="Active Players" value={stats.total_users} icon={Users} testid="kpi-users" />
                            <Kpi label="Active Centres" value={stats.total_centres} icon={Building2} testid="kpi-centres" />
                        </div>

                        <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Total Revenue</div>
                                    <div className="mt-1 font-display text-4xl font-bold text-[#00FF66]" data-testid="kpi-period-revenue">₹{stats.total_revenue}</div>
                                    <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">
                                        {stats.range_start} → {stats.range_end}
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2 items-end">
                                    <div>
                                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Period</label>
                                        <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="period-select"
                                            className="block mt-1 h-9 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                            {PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">Centre</label>
                                        <select value={centreId} onChange={(e) => setCentreId(e.target.value)} data-testid="centre-filter-select"
                                            className="block mt-1 h-9 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                            <option value="">All centres</option>
                                            {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    {period === "custom" && (
                                        <>
                                            <div>
                                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">From</label>
                                                <input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="custom-start"
                                                    className="block mt-1 h-9 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500">To</label>
                                                <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="custom-end"
                                                    className="block mt-1 h-9 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm" />
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            <div className="mt-4 flex items-center gap-4 text-xs">
                                <span className="inline-flex items-center gap-2 text-slate-300">
                                    <span className="w-3 h-3 rounded-sm bg-[#00FF66]" /> Online ₹{stats.total_online_revenue}
                                </span>
                                <span className="inline-flex items-center gap-2 text-slate-300">
                                    <span className="w-3 h-3 rounded-sm bg-[#00FF66]/40 border border-[#00FF66]/60" /> Offline ₹{stats.total_offline_revenue}
                                </span>
                            </div>

                            <div className="mt-4">
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={stats.revenue_chart}>
                                        <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tick={{ fill: "#64748b", fontSize: 10 }} stroke="#334155" />
                                        <YAxis tick={{ fill: "#64748b", fontSize: 10 }} stroke="#334155" />
                                        <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", color: "#fff" }}
                                            formatter={(v, n) => [`₹${v}`, n === "online_revenue" ? "Online" : "Offline"]} />
                                        <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} formatter={(v) => v === "online_revenue" ? "Online" : "Offline"} />
                                        <Bar dataKey="online_revenue" stackId="a" fill="#00FF66" />
                                        <Bar dataKey="offline_revenue" stackId="a" fill="#86efac" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-1 gap-4">
                            <LiveBookingsCard endpoint="/admin/live-bookings" />
                        </div>
                    </>
                )}

                {todayOpen && (
                    <div className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" data-testid="today-modal">
                        <div className="w-full max-w-5xl bg-slate-900 border border-slate-800 rounded-lg p-5 my-6">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div>
                                    <h2 className="font-display text-2xl font-bold uppercase text-white">Today · {todayData?.date || today}</h2>
                                    <p className="text-slate-400 text-xs mt-1">All bookings + revenue collected today.</p>
                                </div>
                                <div className="flex items-center gap-2">
                                    <select value={todayCentre} onChange={(e) => { setTodayCentre(e.target.value); refreshToday(e.target.value); }}
                                        data-testid="today-centre-filter"
                                        className="h-9 px-3 bg-slate-950 border border-slate-700 rounded text-white text-sm">
                                        <option value="">All centres</option>
                                        {centres.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                    <button onClick={() => { setTodayOpen(false); setTodayData(null); }}
                                        data-testid="today-close-btn"
                                        className="h-9 px-3 border border-slate-700 rounded text-slate-300 text-xs font-bold uppercase tracking-widest hover:border-[#00FF66]/50">
                                        Close
                                    </button>
                                </div>
                            </div>

                            {!todayData ? (
                                <div className="text-slate-500 mt-6 text-sm">Loading...</div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-5">
                                        <Mini label="Bookings" value={todayData.total_bookings} testid="today-kpi-bookings" />
                                        <Mini label="Revenue" value={`₹${todayData.total_revenue}`} accent="green" testid="today-kpi-revenue" />
                                        <Mini label="Online" value={`₹${todayData.online_revenue}`} accent="green" testid="today-kpi-online" />
                                        <Mini label="Offline" value={`₹${todayData.offline_revenue}`} accent="lightgreen" testid="today-kpi-offline" />
                                        <Mini label="Cash" value={`₹${todayData.cash_collected}`} testid="today-kpi-cash" />
                                        <Mini label="UPI" value={`₹${todayData.upi_collected}`} testid="today-kpi-upi" />
                                    </div>

                                    {todayData.per_centre.length > 0 && (
                                        <div className="mt-4">
                                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mb-2">Per Centre</div>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                                {todayData.per_centre.map(p => (
                                                    <div key={p.centre} className="bg-slate-950 border border-slate-800 rounded p-3 flex items-center justify-between">
                                                        <div className="text-slate-200 text-sm font-bold uppercase tracking-wider truncate">{p.centre}</div>
                                                        <div className="text-right">
                                                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">{p.count} bookings</div>
                                                            <div className="text-[#00FF66] font-bold">₹{p.revenue}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4 max-h-[400px] overflow-y-auto bg-slate-950 border border-slate-800 rounded">
                                        <table className="w-full text-sm">
                                            <thead className="sticky top-0 bg-slate-950">
                                                <tr className="text-slate-400 text-xs uppercase tracking-widest border-b border-slate-800">
                                                    <th className="text-left py-2 px-3">Code</th>
                                                    <th className="text-left py-2 px-3">Customer</th>
                                                    <th className="text-left py-2 px-3">Centre</th>
                                                    <th className="text-left py-2 px-3">Slot</th>
                                                    <th className="text-right py-2 px-3">Paid</th>
                                                    <th className="text-left py-2 px-3">Source</th>
                                                    <th className="text-left py-2 px-3">Booked by</th>
                                                </tr>
                                            </thead>
                                            <tbody className="text-slate-300">
                                                {todayData.bookings.length === 0 ? (
                                                    <tr><td colSpan={7} className="py-6 text-center text-slate-500">No bookings today{todayCentre ? " for this centre" : ""}.</td></tr>
                                                ) : todayData.bookings.map(b => (
                                                    <tr key={b.id} className="border-b border-slate-900" data-testid={`today-booking-${b.id}`}>
                                                        <td className="py-2 px-3 font-mono text-xs text-[#00FF66]">{b.booking_code}</td>
                                                        <td className="py-2 px-3">{b.user_name}</td>
                                                        <td className="py-2 px-3">{b.centre_name}</td>
                                                        <td className="py-2 px-3 text-xs">{time12h(b.start_time)}–{time12h(b.end_time)}</td>
                                                        <td className="py-2 px-3 text-right font-bold text-white">₹{b.paid_amount}</td>
                                                        <td className="py-2 px-3">
                                                            <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${
                                                                b.source === "offline" ? "bg-[#86efac]/20 text-[#86efac]" : "bg-[#00FF66]/25 text-[#00FF66]"
                                                            }`}>{b.source || "online"}</span>
                                                        </td>
                                                        <td className="py-2 px-3 text-xs text-slate-400">{b.booked_by_name || (b.source === "offline" ? "—" : "Player")}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

function Mini({ label, value, accent, testid }) {
    const cls = accent === "green" ? "border-[#00FF66]/40 text-[#00FF66]" :
                accent === "lightgreen" ? "border-[#86efac]/40 text-[#86efac]" :
                "border-slate-800 text-white";
    return (
        <div className={`bg-slate-950 border ${cls} rounded p-3`} data-testid={testid}>
            <div className="text-[9px] font-bold tracking-[0.25em] uppercase text-slate-500">{label}</div>
            <div className={`mt-1 font-display text-lg font-bold ${accent === "green" ? "text-[#00FF66]" : accent === "lightgreen" ? "text-[#86efac]" : "text-white"}`}>{value}</div>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, highlight, testid, onClick, hint }) {
    const clickable = !!onClick;
    return (
        <div onClick={onClick}
            className={`bg-slate-900 border ${highlight ? "border-[#00FF66]/40" : "border-slate-800"} rounded-lg p-4 sm:p-5 ${clickable ? "cursor-pointer hover:border-[#00FF66]/70 transition" : ""}`}
            data-testid={testid}>
            <div className="flex items-center justify-between">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400">{label}</div>
                <Icon className={`w-4 h-4 ${highlight ? "text-[#00FF66]" : "text-slate-500"}`} />
            </div>
            <div className={`mt-2 font-display text-3xl font-bold ${highlight ? "text-[#00FF66]" : "text-white"}`}>{value}</div>
            {hint && clickable && (
                <div className="mt-1 text-[10px] uppercase tracking-widest text-slate-500">{hint} →</div>
            )}
        </div>
    );
}

function Card({ title, children, className = "" }) {
    return (
        <div className={`bg-slate-900 border border-slate-800 rounded-lg p-4 sm:p-5 ${className}`}>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-4">{title}</div>
            {children}
        </div>
    );
}

function PreviewAsUserButton() {
    const { previewAsUser } = useAuth();
    const navigate = useNavigate();
    const onClick = () => {
        previewAsUser();
        navigate("/");
    };
    return (
        <button onClick={onClick} data-testid="preview-as-user-btn"
            className="inline-flex items-center gap-2 px-3 h-10 bg-amber-500/15 border border-amber-500/50 text-amber-300 font-bold uppercase tracking-widest text-xs rounded hover:bg-amber-500/25">
            <Eye className="w-4 h-4" /> Preview as User
        </button>
    );
}
